import * as i0 from '@angular/core';
import { InjectionToken, makeEnvironmentProviders, inject, DestroyRef, PLATFORM_ID, Inject, Injectable, HostListener, Input, Directive } from '@angular/core';
import * as i1 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { tap, filter, map, delay } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import * as i2 from '@angular/common';
import { isPlatformServer } from '@angular/common';
class TrackEventAction {
  constructor(values) {
    this.action = values.action;
    this.category = values.category;
    this.value = values.value;
    this.additionalProviderProperties = values.additionalProviderProperties || {};
  }
}
class TrackEventOpenLink {
  constructor(values) {
    this.href = values.href;
    this.action = values.action;
    this.category = values.category || "";
    this.value = values.value;
    this.additionalProviderProperties = values.additionalProviderProperties || {};
  }
}
class TrackEventDownload {
  constructor(values) {
    this.action = values.action;
    this.name = values.name;
    this.category = values.category || "";
    this.value = values.value;
    this.additionalProviderProperties = values.additionalProviderProperties || {};
  }
}
class TrackEventShoppingCart {
  constructor(values) {
    this.items = values.items;
    this.totalPrice = values.totalPrice;
    this.category = values.category || "";
    this.value = values.value;
    this.additionalProviderProperties = values.additionalProviderProperties || {};
  }
}
class TrackEventOrder {
  constructor(values) {
    this.orderId = values.orderId;
    this.items = values.items;
    this.totalPrice = values.totalPrice;
    this.discount = values.discount;
    this.category = values.category || "";
    this.value = values.value;
    this.additionalProviderProperties = values.additionalProviderProperties || {};
  }
}
class TrackEventProductView {
  constructor(values) {
    this.productSKU = values.productSKU;
    this.productName = values.productName;
    this.categoryName = values.categoryName;
    this.price = values.price;
    this.category = values.category || "";
    this.value = values.value;
    this.additionalProviderProperties = values.additionalProviderProperties || {};
  }
}
class TrackEventCategoryView {
  constructor(values) {
    this.categoryName = values.categoryName;
    this.category = values.category || "";
    this.value = values.value;
    this.additionalProviderProperties = values.additionalProviderProperties || {};
  }
}
class TrackEventSiteSearch {
  constructor(values) {
    this.searchWord = values.searchWord;
    this.category = values.category || "";
    this.value = values.value;
    this.additionalProviderProperties = {};
  }
}
class MatomoProvider {
  static get providerName() {
    return "matomo";
  }
  getProvicerName() {
    return MatomoProvider.providerName;
  }
  setUser(user) {
    try {
      _paq.push(['setUserId', user]);
    } catch (e) {
      if (!(e instanceof ReferenceError)) {
        throw e;
      }
    }
  }
  trackPageView(ev) {
    try {
      _paq.push(['setDocumentTitle', ev.title]);
      _paq.push(['setCustomUrl', ev.url]);
      _paq.push(['trackPageView']);
    } catch (e) {
      if (!(e instanceof ReferenceError)) {
        throw e;
      }
    }
  }
  trackEvent(event) {
    let params = [];
    if (event instanceof TrackEventOpenLink) {
      params = ['trackEvent', 'openlink', event.action, event.href, event.value];
    } else if (event instanceof TrackEventDownload) {
      params = ['trackEvent', 'download', event.action, event.name, event.value];
    } else if (event instanceof TrackEventAction) {
      var props = event.additionalProviderProperties[this.getProvicerName()] || {};
      params = ['trackEvent', event.category, event.action, props.name, event.value];
    } else if (event instanceof TrackEventShoppingCart) {
      // Fügt alle Items in das Matomo Tracking ein.
      this._trackEcommerceItems(event.items);
      // Sendet alles an Matomo.
      params = ["trackEcommerceCartUpdate", event.totalPrice];
    } else if (event instanceof TrackEventOrder) {
      // Fügt alle Items in das Matomo Tracking ein.
      this._trackEcommerceItems(event.items);
      // Sendet alles an Matomo.
      params = ['trackEcommerceOrder', event.orderId, event.totalPrice, undefined, undefined, undefined, event.discount ? event.discount : false];
    } else if (event instanceof TrackEventProductView) {
      params = ['setEcommerceView', event.productSKU, event.productName, event.categoryName, event.price];
    } else if (event instanceof TrackEventCategoryView) {
      params = ['setEcommerceView', false, false, event.categoryName];
    } else if (event instanceof TrackEventSiteSearch) {
      params = ["trackSiteSearch", event.searchWord, event.category || false, event.value ?? false];
    } else {
      var properties = event.additionalProviderProperties[this.getProvicerName()];
      params = ['trackEvent', event.category, properties.action, properties.name, event.value];
    }
    try {
      _paq.push(params);
    } catch (e) {
      if (!(e instanceof ReferenceError)) {
        throw e;
      }
    }
  }
  _trackEcommerceItems(items) {
    for (const item of items) {
      try {
        _paq.push(['addEcommerceItem', item.productSKU, item.productName, item.categoryName, item.price, item.quantity]);
      } catch (e) {
        if (!(e instanceof ReferenceError)) {
          throw e;
        }
      }
    }
  }
}
class MauticProvider {
  constructor() {
    this._user = "";
  }
  setUser(user) {
    this._user = user;
  }
  static get providerName() {
    return "mautic";
  }
  getProvicerName() {
    return MauticProvider.providerName;
  }
  trackPageView(ev) {
    try {
      var tracking = {
        page_title: ev.title
      };
      if (this._user) {
        tracking.email = this._user;
        this._user = "";
      }
      mt("send", "pageview", tracking);
    } catch (e) {
      if (!(e instanceof ReferenceError)) {
        throw e;
      }
    }
  }
  trackEvent(event) {
    var props = null;
    if (event instanceof TrackEventDownload) props = {
      page_title: event.name
    };else props = event.additionalProviderProperties[this.getProvicerName()];
    if (props) {
      if (this._user) props.email = this._user;
      try {
        mt("send", "pageview", props);
      } catch (e) {
        if (!(e instanceof ReferenceError)) {
          throw e;
        }
      }
    }
  }
}
function matomoProvider() {
  return new MatomoProvider();
}
function mauticProvider() {
  return new MauticProvider();
}
class AnalyticsSettings {
  constructor() {
    this.providers = [matomoProvider, mauticProvider];
    this.developerMode = false;
    this.excludedRoutes = [];
    this.enableContactLinkTracking = true;
  }
}
const ESDERS_ANALYTICS_TOKEN = new InjectionToken('ESDERS_ANALYTICS');
function provideAnalytics(settings = {}) {
  const provides = [{
    provide: ESDERS_ANALYTICS_TOKEN,
    useValue: {
      settings
    }
  }, AnalyticsService];
  return makeEnvironmentProviders(provides);
}
let AnalyticsService = /*#__PURE__*/(() => {
  class AnalyticsService {
    constructor(_router, setup, platformId, _locationStrategy) {
      this._router = _router;
      this.platformId = platformId;
      this._locationStrategy = _locationStrategy;
      this._providers = [];
      this._destroyRef = inject(DestroyRef);
      this._trackedLinks = [];
      this._shoppingCardItems = {};
      const defaultConfig = new AnalyticsSettings();
      this.settings = {
        ...defaultConfig,
        ...setup.settings
      };
      // Initialisiert alle Providers.
      this._initProviders();
      if (!isPlatformServer(this.platformId)) {
        fromEvent(window, 'beforeunload').pipe(tap(() => this.flushShoppingCartItems())).subscribe();
      }
    }
    _trackLink(node) {
      const THIZ = this;
      if (node instanceof HTMLAnchorElement && !this._trackedLinks.find(x => x == node)) {
        if (node.href.startsWith("tel:")) {
          node.addEventListener("click", e => {
            THIZ.trackEvent(new TrackEventOpenLink({
              href: e.target.href,
              action: "tel"
            }));
          });
          this._trackedLinks.push(node);
        } else if (node.href.startsWith("mailto:")) {
          node.addEventListener("click", e => {
            THIZ.trackEvent(new TrackEventOpenLink({
              href: e.target.href,
              action: "mail"
            }));
          });
          this._trackedLinks.push(node);
        }
      }
    }
    /**
     * @brief Schickt den Warenkorb los.
     */
    flushShoppingCartItems() {
      for (const key of Object.keys(this._shoppingCardItems)) {
        var totalPrice = this._shoppingCardItems[key].reduce((sum, x) => sum + (x.price || 0) * x.quantity, 0);
        var ev = new TrackEventShoppingCart({
          items: this._shoppingCardItems[key],
          totalPrice: totalPrice
        });
        this.trackEvent(ev, key);
      }
    }
    /**
     * @brief Löscht den Warenkorb aus dem Tracking
     */
    clearShoppingCartItems() {
      this._shoppingCardItems = {};
    }
    /**
     * @brief Startet das Tracking.
     */
    startTracking() {
      this._router.events.pipe(filter(e => e instanceof NavigationEnd), filter(() => !this.settings.developerMode), map(e => {
        return {
          url: e.urlAfterRedirects
        };
      }), delay(0), takeUntilDestroyed(this._destroyRef)).subscribe(url => this.trackPageView(url.url));
      // Tracked alle Links mit Telefonnummern und E-Mails
      if (this.settings.enableContactLinkTracking) this.trackContactLinks();
    }
    /**
     * @brief Erlaubt das Tracken aller tel: und mailto: links
     */
    trackContactLinks() {
      if (!isPlatformServer(this.platformId)) {
        const THIZ = this;
        document.querySelectorAll("a[href^='tel:']").forEach(node => THIZ._trackLink(node));
        document.querySelectorAll("a[href^='mailto:']").forEach(node => THIZ._trackLink(node));
        this._domChanges = new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            if (mutation.type === 'childList') {
              mutation.addedNodes.forEach(node => {
                THIZ._trackLink(node);
              });
              mutation.removedNodes.forEach(node => {
                if (node instanceof HTMLAnchorElement) THIZ._trackedLinks = THIZ._trackedLinks.filter(x => x != node);
              });
            } else if (mutation.type === "attributes" && mutation.attributeName == "href") {
              THIZ._trackLink(mutation.target);
            }
          });
        });
        this._domChanges.observe(document.body, {
          subtree: true,
          childList: true,
          attributes: true,
          characterData: true
        });
      }
    }
    /**
     * @brief Setzt den User.
     * @param user
     */
    setUser(user) {
      for (const provider of this._providers) provider.setUser(user);
    }
    /**
     * @brief Tracked die aktuell aufgerufende Seite.
     * @param path
     */
    trackPageView(path, provider) {
      if (this._matchesExcludedRoute(path)) return;
      if (!window.location.origin) {
        window.location.origin = window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':' + window.location.port : '');
      }
      if (path.slice(0, 1) == "/") path = path.substring(1);
      // Base holen
      var base = this._locationStrategy.getBaseHref();
      if (base.slice(0, 1) != "/") base = "/" + base;
      if (base.slice(base.length - 1) != "/") base += "/";
      var ev = {
        title: window.document.title,
        url: window.location.origin + base + path
      };
      var providers = this._providers.filter(x => !provider || x.getProvicerName() == provider);
      for (const provider of providers) provider.trackPageView(ev);
    }
    trackEvent(event, provider) {
      var providers = this._providers.filter(x => !provider || x.getProvicerName() == provider);
      for (const provider of providers) provider.trackEvent(event);
    }
    trackShoppingCartItem(item, provider) {
      if (!this._shoppingCardItems[provider || ""]) this._shoppingCardItems[provider || ""] = [];
      var existingItem = this._shoppingCardItems[provider || ""].find(x => x.productSKU == item.productSKU);
      if (existingItem) {
        existingItem.categoryName = item.categoryName;
        existingItem.productName = item.productName;
        existingItem.price = item.price;
        existingItem.quantity = item.quantity;
        if (existingItem.quantity <= 0) this._shoppingCardItems[provider || ""] = this._shoppingCardItems[provider || ""].filter(x => x.productSKU != item.productSKU);
      } else if (item.quantity > 0) this._shoppingCardItems[provider || ""].push(item);
    }
    _initProviders() {
      for (const factory of this.settings.providers) this._providers.push(factory());
      if (this._providers.length == 0) console.error("No providers given!");
    }
    _matchesExcludedRoute(url) {
      for (const excludedRoute of this.settings.excludedRoutes) {
        const matchesRegex = excludedRoute instanceof RegExp && excludedRoute.test(url);
        if (matchesRegex || url.indexOf(excludedRoute) !== -1) {
          return true;
        }
      }
      return false;
    }
    static {
      this.ɵfac = function AnalyticsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AnalyticsService)(i0.ɵɵinject(i1.Router), i0.ɵɵinject(ESDERS_ANALYTICS_TOKEN), i0.ɵɵinject(PLATFORM_ID), i0.ɵɵinject(i2.LocationStrategy));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AnalyticsService,
        factory: AnalyticsService.ɵfac
      });
    }
  }
  return AnalyticsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TrackActionDirective = /*#__PURE__*/(() => {
  class TrackActionDirective {
    constructor(_analyticsService, _el) {
      this._analyticsService = _analyticsService;
      this._el = _el;
    }
    onClick() {
      this._analyticsService.trackEvent(new TrackEventAction({
        action: this.trackAction,
        category: this.trackCategory,
        value: this.trackRevenue
      }), this.trackProvider);
      return true;
    }
    static {
      this.ɵfac = function TrackActionDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TrackActionDirective)(i0.ɵɵdirectiveInject(AnalyticsService), i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TrackActionDirective,
        selectors: [["", "trackAction", ""]],
        hostBindings: function TrackActionDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function TrackActionDirective_click_HostBindingHandler($event) {
              return ctx.onClick($event);
            });
          }
        },
        inputs: {
          trackProvider: "trackProvider",
          trackRevenue: "trackRevenue",
          trackAction: "trackAction",
          trackCategory: "trackCategory"
        }
      });
    }
  }
  return TrackActionDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TrackDownloadDirective = /*#__PURE__*/(() => {
  class TrackDownloadDirective {
    constructor(_analyticsService, _el) {
      this._analyticsService = _analyticsService;
      this._el = _el;
      this._isLink = false;
      const tagName = this._el.nativeElement.tagName?.toLowerCase();
      if (tagName == "a" || tagName == "area") this._isLink = true;
    }
    onClick() {
      var name = "";
      if (this._isLink) {
        var a = this._el.nativeElement;
        var lastSlash = a.href.lastIndexOf("/") + 1;
        name = a.download || a.href.substring(lastSlash);
      } else if (this.download) name = this.download;
      var ev = new TrackEventDownload({
        name: name,
        action: this.trackDownload,
        value: this.trackRevenue
      });
      if (ev.name) this._analyticsService.trackEvent(ev, this.trackProvider);
      return true;
    }
    static {
      this.ɵfac = function TrackDownloadDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TrackDownloadDirective)(i0.ɵɵdirectiveInject(AnalyticsService), i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TrackDownloadDirective,
        selectors: [["", "trackDownload", ""]],
        hostBindings: function TrackDownloadDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function TrackDownloadDirective_click_HostBindingHandler($event) {
              return ctx.onClick($event);
            });
          }
        },
        inputs: {
          trackProvider: "trackProvider",
          download: "download",
          trackRevenue: "trackRevenue",
          trackDownload: "trackDownload"
        }
      });
    }
  }
  return TrackDownloadDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TrackLinkDirective = /*#__PURE__*/(() => {
  class TrackLinkDirective {
    get href() {
      return this._href;
    }
    set href(value) {
      this._href = value;
      if (this._isLink) this._el.nativeElement.href = this._href;
    }
    constructor(_analyticsService, _el) {
      this._analyticsService = _analyticsService;
      this._el = _el;
      this._href = "";
      this._isLink = false;
      const tagName = this._el.nativeElement.tagName?.toLowerCase();
      if (tagName == "a" || tagName == "area") this._isLink = true;
    }
    onClick() {
      var href = "";
      if (this._isLink) href = this._el.nativeElement.href;else if (this.href) href = this.href;
      var ev = new TrackEventOpenLink({
        href: href,
        action: this.trackLink,
        value: this.trackRevenue
      });
      if (ev.href) this._analyticsService.trackEvent(ev, this.trackProvider);
      return true;
    }
    static {
      this.ɵfac = function TrackLinkDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TrackLinkDirective)(i0.ɵɵdirectiveInject(AnalyticsService), i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TrackLinkDirective,
        selectors: [["", "trackLink", ""]],
        hostBindings: function TrackLinkDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function TrackLinkDirective_click_HostBindingHandler($event) {
              return ctx.onClick($event);
            });
          }
        },
        inputs: {
          trackProvider: "trackProvider",
          href: "href",
          trackRevenue: "trackRevenue",
          trackLink: "trackLink"
        }
      });
    }
  }
  return TrackLinkDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TrackShoppingCartItemDirective = /*#__PURE__*/(() => {
  class TrackShoppingCartItemDirective {
    constructor(_analyticsService, _el) {
      this._analyticsService = _analyticsService;
      this._el = _el;
    }
    onClick() {
      var item = {
        productSKU: this.trackShoppingCartItem,
        productName: this.trackName,
        categoryName: this.trackCategory,
        price: this.trackPrice,
        quantity: this.trackQuantity
      };
      this._analyticsService.trackShoppingCartItem(item);
      // this._analyticsService.trackEvent(new TrackEventAction({
      //   action: this.trackAction,
      //   category: this.trackCategory,
      //   value: this.trackRevenue
      // }), this.trackProvider);
      return true;
    }
    static {
      this.ɵfac = function TrackShoppingCartItemDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TrackShoppingCartItemDirective)(i0.ɵɵdirectiveInject(AnalyticsService), i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TrackShoppingCartItemDirective,
        selectors: [["", "trackShoppingCartItem", ""]],
        hostBindings: function TrackShoppingCartItemDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function TrackShoppingCartItemDirective_click_HostBindingHandler($event) {
              return ctx.onClick($event);
            });
          }
        },
        inputs: {
          trackProvider: "trackProvider",
          trackPrice: "trackPrice",
          trackCategory: "trackCategory",
          trackShoppingCartItem: "trackShoppingCartItem",
          trackName: "trackName",
          trackQuantity: "trackQuantity"
        }
      });
    }
  }
  return TrackShoppingCartItemDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of analytics
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AnalyticsService, AnalyticsSettings, ESDERS_ANALYTICS_TOKEN, MatomoProvider, MauticProvider, TrackActionDirective, TrackDownloadDirective, TrackEventAction, TrackEventCategoryView, TrackEventDownload, TrackEventOpenLink, TrackEventOrder, TrackEventProductView, TrackEventShoppingCart, TrackEventSiteSearch, TrackLinkDirective, TrackShoppingCartItemDirective, matomoProvider, mauticProvider, provideAnalytics };
